// (4) Pre-session briefing: formulas to brush up + the topic's common errors at
// this school → Start.
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '@/lib/api';
import { isFirstParty } from '@/lib/tenant';
import type { FormulaItem, PracticeTopic, TopicCommonErrorsDTO } from '@/types';
import { Card } from '@/components/ds';

/** "Updated 5 hours ago" — the summary is rebuilt nightly, so hours and days suffice. */
function updatedAgo(iso: string): string {
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  const rtf = new Intl.RelativeTimeFormat('en-IN', { numeric: 'auto' });
  if (minutes < 60) return `Updated ${rtf.format(-Math.max(minutes, 1), 'minute')}`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `Updated ${rtf.format(-hours, 'hour')}`;
  return `Updated ${rtf.format(-Math.round(hours / 24), 'day')}`;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

function CommonErrors({ topic }: { topic: PracticeTopic }) {
  const query = useQuery({
    queryKey: ['practice-common-errors', topic.topicId],
    queryFn: () => apiGet<TopicCommonErrorsDTO>(`/practice/topics/${topic.topicId}/common-errors`),
  });
  const data = query.data;
  // The pool is the workspace: a school's own students, or everyone practising
  // on CWY directly.
  const where = isFirstParty ? 'among students on CWY' : 'at your school';
  const peers = isFirstParty ? 'a few students' : 'a few classmates';

  return (
    <Card
      title={`Common errors in ${topic.name} ${where}`}
      hint={data?.refreshedAt ? updatedAgo(data.refreshedAt) : undefined}
    >
      {query.isLoading ? (
        <p className="text-sm" style={{ color: 'var(--text-hint)' }}>
          Loading…
        </p>
      ) : query.isError ? (
        <p className="text-sm" style={{ color: 'var(--text-hint)' }}>
          Common errors could not be loaded right now. You can still start practising.
        </p>
      ) : !data || data.errors.length === 0 ? (
        <p className="text-sm" style={{ color: 'var(--text-hint)' }}>
          Not enough practice on this topic yet to spot a pattern. Common errors appear here once{' '}
          {peers} have practised it.
        </p>
      ) : (
        <>
          <ol className="space-y-3">
            {data.errors.map((e) => (
              <li key={e.title} className="text-sm">
                <p className="font-semibold" style={{ color: 'var(--text-body)' }}>
                  {e.title}
                </p>
                <p className="mt-0.5" style={{ color: 'var(--ink-2)' }}>
                  {e.misconception}
                </p>
                <p className="mt-0.5" style={{ color: 'var(--ink-2)' }}>
                  <span className="font-semibold" style={{ color: 'var(--text-accent)' }}>
                    How to avoid it:
                  </span>{' '}
                  {e.howToAvoid}
                </p>
                <p className="mt-0.5 text-xs" style={{ color: 'var(--text-hint)' }} data-numeric="">
                  {plural(e.students, 'student', 'students')} ·{' '}
                  {plural(e.questionIds.length, 'question', 'questions')}
                </p>
              </li>
            ))}
          </ol>
          <p className="mt-3 text-xs" style={{ color: 'var(--text-hint)' }} data-numeric="">
            From {plural(data.sampleSize, 'wrong answer', 'wrong answers')} by{' '}
            {plural(data.studentCount, 'student', 'students')} in Practice over the last 90 days.
          </p>
        </>
      )}
    </Card>
  );
}

export function PreSessionBriefing({
  topic,
  onStart,
}: {
  topic: PracticeTopic;
  onStart: () => void;
}) {
  const formulas = useQuery({
    queryKey: ['practice-formulas'],
    queryFn: () => apiGet<FormulaItem[]>('/practice/formulas'),
  });

  return (
    <div className="space-y-4">
      {/* Formulas to brush up */}
      <section
        className="rounded-2xl p-4"
        style={{ background: 'var(--surface-card)', border: '1px solid var(--border-default)' }}
      >
        <h3 className="mb-2 text-sm font-bold" style={{ color: 'var(--text-body)' }}>
          Formulas to brush up
        </h3>
        {formulas.isLoading ? (
          <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
            Loading…
          </p>
        ) : (formulas.data ?? []).length === 0 ? (
          <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
            Nothing flagged — you're on top of your formulas!
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {(formulas.data ?? []).map((f) => (
              <span
                key={f.subtopicId}
                className="rounded-full px-2.5 py-1 text-xs font-semibold"
                style={{ background: 'var(--orange-bg)', color: 'var(--orange-text)' }}
              >
                {f.subtopicName}
                {f.theoryRef ? ` · ${f.theoryRef}` : ''}
              </span>
            ))}
          </div>
        )}
      </section>

      <CommonErrors topic={topic} />

      <button
        onClick={onStart}
        className="w-full rounded-2xl px-4 py-3 font-bold text-white transition"
        style={{ background: 'var(--action-bg)' }}
      >
        Start practice
      </button>
    </div>
  );
}
